import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  LinearProgress,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState, useMemo, useRef, useEffect, useCallback, SyntheticEvent } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { UploadStreamResponse } from '@/types/document';
import { VaultDocument } from '@/types/vault';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useRenameDocument } from '@/api/endpoints/document/useRenameDocument';
import { useUpdateCategoryOfDocument } from '@/api/endpoints/document/useUpdateCategoryOfDocument';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { useGetVaultDocuments } from '@/api/endpoints/vault/useGetVaultDocuments';
import { FilterLinesIcon, RotateCcwIcon } from '@/assets/icons';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { EditIcon } from '@/assets/icons/EditIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';
import { formatFullDate } from '@/utils/date';

import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';
import DocumentViewModal from '@/components/document/DocumentViewModal';
import VaultDocumentTagModal from '@/components/vault/VaultDocumentTagModal';

const TABLE_HEAD = [
  { id: 'name', label: 'Document Name', sortable: true, width: '30%' },
  { id: 'type', label: 'Document Category', sortable: false, width: '25%' },
  { id: 'tags', label: 'Tags', sortable: false, width: '30%' },
  { id: 'date', label: 'Date Added', sortable: true, width: '15%' },
];

// Add helper function to format document name
const formatDocumentName = (name: string, maxLength = 50) => {
  if (name.length <= maxLength) return name;

  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${name.slice(0, maxLength - 3)}...`;
  }

  const extension = name.slice(lastDotIndex);
  const nameWithoutExtension = name.slice(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // 3 for "..."

  return `${nameWithoutExtension.slice(0, maxNameLength)}...${extension}`;
};

type Order = 'asc' | 'desc';
type Document = VaultDocument;

// Union type for both real documents and temporary uploads
type DocumentOrUpload =
  | VaultDocument
  | {
      id: string;
      original_name: string;
      created_at: string;
      category_id: null;
      tags: null;
      isTemporary: true;
      uploadProgress: { message: string; progress: number; lastUpdate?: number };
    };

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

// Custom comparator for DocumentOrUpload union type
function documentComparator(
  a: DocumentOrUpload,
  b: DocumentOrUpload,
  order: Order,
  orderBy: keyof Document
) {
  // Temporary uploads always go first
  const aIsTemp = 'isTemporary' in a;
  const bIsTemp = 'isTemporary' in b;

  if (aIsTemp && !bIsTemp) return -1;
  if (!aIsTemp && bIsTemp) return 1;
  if (aIsTemp && bIsTemp) return 0; // Keep temporary uploads in order they were added

  // Both are real documents, use normal comparison
  return getComparator<Document>(order, orderBy)(a as Document, b as Document);
}

// Helper function to calculate progress based on status
const getProgressByStatus = (status: string): number => {
  switch (status) {
  case 'completed':
    return 100;
  case 'processing':
    return 75;
  case 'uploaded':
    return 50;
  case 'uploading':
    return 25;
  default:
    return 0;
  }
};

// Helper function to get status message
const getStatusMessage = (response: UploadStreamResponse): string => {
  if (response.message) {
    return response.message;
  }

  switch (response.status) {
  case 'starting':
    return 'Starting upload...';
  case 'uploading':
    return 'Uploading file to cloud storage...';
  case 'uploaded':
    return 'File uploaded, preparing for AI processing...';
  case 'tagged':
    return 'Applying document tags...';
  case 'validating':
    return 'Validating document...';
  case 'processing':
    return 'AI processing document...';
  case 'completed':
    return 'Processing completed!';
  case 'connected':
    return 'Connected to processing service...';
  default:
    return 'Processing...';
  }
};

interface DocumentNameEditorProps {
  initialValue: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

const DocumentNameEditor: React.FC<DocumentNameEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (inputRef.current && inputRef.current.value.trim()) {
        onSave(inputRef.current.value.trim());
      } else {
        onCancel();
      }
    } else if (event.key === 'Escape') {
      onCancel();
    }
    event.stopPropagation();
  };

  const handleBlur = () => {
    if (inputRef.current && inputRef.current.value.trim()) {
      onSave(inputRef.current.value.trim());
    } else {
      onCancel();
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <input
      ref={inputRef}
      defaultValue={initialValue}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={handleClick}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #1976d2',
        borderRadius: '4px',
        padding: '0 8px',
        fontSize: 'inherit',
        outline: 'none',
      }}
    />
  );
};

interface FilterState {
  category: string;
  tags: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

interface DropZoneProps {
  onFileDrop: (file: File) => void;
  isUploading: boolean;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDrop, isUploading, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (isUploading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        onFileDrop(file);
      }
    },
    [onFileDrop, isUploading]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&::after': isDragging
          ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 10,
            pointerEvents: 'none',
          }
          : {},
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 11,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <UploadCloud01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
            Drop file to upload
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const VaultPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const location = useLocation();
  const history = useHistory();
  const pendingCategoryIdRef = useRef<number | null>(null);
  const dialogOpenedRef = useRef<boolean>(false);
  const documentUploadButtonRef = useRef<DocumentUploadButtonAPI>(null);

  // --- State Hooks ---
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Document>('created_at');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    category: '',
    tags: [],
    dateRange: {
      start: null,
      end: null,
    },
  });
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: { message: string; progress: number; lastUpdate?: number };
  }>({});
  const [, setPendingUploads] = useState<Set<string>>(new Set());

  // Temporary upload entries to show in the list immediately
  const [temporaryUploads, setTemporaryUploads] = useState<{
    [key: string]: {
      id: string;
      original_name: string;
      created_at: string;
      category_id: null;
      tags: null;
      isTemporary: true;
      uploadProgress: { message: string; progress: number; lastUpdate?: number };
    };
  }>({});

  // --- API Hooks ---
  const {
    data: documents = [],
    refetch,
    isLoading,
  } = useGetVaultDocuments(
    {
      property_location_id: selectedPropertyLocation?.id,
    },
    !!session?.access_token && !!selectedPropertyLocation?.id
  );

  const { data: categories = [] } = useGetDocumentCategories();

  const updateCategoryOfDocumentMutation = useUpdateCategoryOfDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const renameDocumentMutation = useRenameDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      refetch();
    },
  });

  // Single useDocumentUploadV2 instance for streaming upload and processing
  const { uploadFile: uploadWithStreaming, isUploading } = useDocumentUploadV2({
    onProgress: (response: UploadStreamResponse) => {
      // If status is completed but no document_id, trigger a refetch to get the document
      if (response.status === 'completed' && !response.document_id) {
        setTemporaryUploads({});
        refetch();
        return;
      }

      // Handle progress updates
      if (response.document_id) {
        const documentId = response.document_id;

        // Clear any temporary uploads since we now have a document ID
        setTemporaryUploads(prev => {
          if (Object.keys(prev).length > 0) {
            return {};
          }
          return prev;
        });

        // Add to processing documents if not already there
        setProcessingDocuments(prev => {
          const newSet = new Set(prev);
          const wasNew = !newSet.has(documentId);
          newSet.add(documentId);

          // Refetch on first time we see this document ID (document was created)
          if (wasNew) {
            refetch();
          }

          return newSet;
        });

        setUploadProgress(prev => ({
          ...prev,
          [documentId]: {
            message: response.message || 'Processing document...',
            progress: response.progress ? response.progress * 100 : 0,
            lastUpdate: Date.now(),
          },
        }));
      } else {
        // Update temporary uploads with progress
        const statusMessage = getStatusMessage(response);

        setTemporaryUploads(prev => {
          const updated = { ...prev };

          // Update all temporary uploads with the progress
          Object.keys(updated).forEach(key => {
            const progressValue = response.progress
              ? response.progress * 100
              : getProgressByStatus(response.status);

            updated[key] = {
              ...updated[key],
              uploadProgress: {
                message: statusMessage,
                progress: progressValue,
                lastUpdate: Date.now(),
              },
            };
          });
          return updated;
        });
      }
    },
    onComplete: (response: UploadStreamResponse) => {
      if (response.document_id) {
        const documentId = response.document_id;
        setUploadProgress(prev => ({
          ...prev,
          [documentId]: {
            message: 'Processing completed!',
            progress: 100,
            lastUpdate: Date.now(),
          },
        }));

        // Clean up processing state after delay and refetch again to get final status
        setTimeout(() => {
          setProcessingDocuments(prev => {
            const newSet = new Set(prev);
            newSet.delete(documentId);
            return newSet;
          });
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[documentId];
            return newProgress;
          });
          refetch();
        }, 2000);
      } else {
        // No document_id in completion, clean up temporary uploads and refetch
        setTemporaryUploads({});
        setPendingUploads(new Set());

        // Refetch to get the new document that should have been created
        setTimeout(() => {
          refetch();
        }, 1000);
      }
    },
    onError: (errorMessage: string) => {
      // Improved error handling for timeouts
      if (errorMessage.includes('Upload timed out') || errorMessage.includes('502')) {
        enqueueSnackbar(
          'Upload is taking longer than expected. The file has been uploaded and is being processed in the background. Please refresh the page in a few minutes to see the document.',
          {
            variant: 'warning',
            persist: true,
            action: (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setTemporaryUploads({});
                  refetch();
                }}
              >
                Refresh Now
              </Button>
            ),
          }
        );
      } else {
        enqueueSnackbar(`Upload failed: ${errorMessage}`, { variant: 'error' });
      }

      // Clean up any processing state, pending uploads, and temporary uploads
      setProcessingDocuments(new Set());
      setUploadProgress({});
      setPendingUploads(new Set());
      setTemporaryUploads({});
    },
  });

  // --- Memoized Data ---
  const filteredDocs = useMemo(() => {
    const realDocs = documents.filter(doc => {
      // Name filter
      const nameMatch = doc.original_name.toLowerCase().includes(filterName.toLowerCase());

      // Category filter
      const categoryMatch = !filters.category || doc.category_id?.toString() === filters.category;

      // Tags filter
      const tagsMatch =
        filters.tags.length === 0 ||
        filters.tags.every(filterTag =>
          doc.tags?.some(tag => {
            let tagName = '';
            if (tag.property_location_id) {
              const propertyLocation = doc.property_locations?.find(
                l => String(l?.id) === String(tag.property_location_id)
              );
              tagName = propertyLocation?.name || '';
            } else if (tag.building_id) {
              const building = doc.buildings?.find(b => String(b?.id) === String(tag.building_id));
              tagName = building?.name || '';
            } else if (tag.unit_id) {
              const unit = doc.units?.find(u => String(u?.id) === String(tag.unit_id));
              tagName = unit?.name || '';
            } else if (tag.tenant_id) {
              const tenant = doc.tenants?.find(t => String(t?.id) === String(tag.tenant_id));
              tagName = tenant?.full_name || '';
            } else if (tag.property_manager_id) {
              const manager = doc.property_managers?.find(
                m => String(m?.id) === String(tag.property_manager_id)
              );
              tagName = manager?.limitedUser.full_name || '';
            }
            return tagName && filters.tags.includes(tagName);
          })
        );

      // Date range filter
      const docDate = new Date(doc.created_at);
      const dateMatch =
        (!filters.dateRange.start || docDate >= filters.dateRange.start) &&
        (!filters.dateRange.end || docDate <= filters.dateRange.end);

      return nameMatch && categoryMatch && tagsMatch && dateMatch;
    });

    // Add temporary uploads at the beginning of the list
    const tempUploads = Object.values(temporaryUploads);
    return [...tempUploads, ...realDocs];
  }, [documents, filterName, filters, temporaryUploads]);

  const sortedDocs = useMemo(
    () => [...filteredDocs].sort((a, b) => documentComparator(a, b, order, orderBy)),
    [filteredDocs, order, orderBy]
  );

  const displayedDocs = useMemo(
    () => sortedDocs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedDocs, page, rowsPerPage]
  );

  // --- Handlers ---
  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property: keyof Document) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: SyntheticEvent | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleDeleteClick = (documentId: string) => {
    setDocumentToDelete(documentId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleCategoryChange = (id: string, newCategory: string) => {
    updateCategoryOfDocumentMutation.mutate({
      categoryId: Number(newCategory),
      id,
    });
  };

  const handleStartEditing = useCallback((document: DocumentOrUpload) => {
    // Only allow editing real documents, not temporary uploads
    if ('isTemporary' in document) return;
    setEditingDocumentId(document.id);
  }, []);

  const handleSaveDocumentName = (newName: string) => {
    if (editingDocumentId && newName) {
      renameDocumentMutation.mutate({
        id: editingDocumentId,
        name: newName,
      });
      setEditingDocumentId(null);
    }
  };

  const handleCancelEditing = () => {
    setEditingDocumentId(null);
  };

  const handleTagDocument = (document: DocumentOrUpload) => {
    // Only allow tagging real documents, not temporary uploads
    if ('isTemporary' in document) return;
    setSelectedDocument(document);
    setIsTagModalOpen(true);
  };

  const handleTagModalClose = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
  };

  const handleTagModalSuccess = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
    refetch();
  };

  const handleFilterClick = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterClose = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterApply = (newFilters: FilterState) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
  };

  const handleFilterReset = () => {
    setFilters({
      category: '',
      tags: [],
      dateRange: {
        start: null,
        end: null,
      },
    });
  };

  const handlePreviewDocument = useCallback((document: DocumentOrUpload) => {
    // Only allow previewing real documents, not temporary uploads
    if ('isTemporary' in document) return;
    setPreviewDocumentId(document.id);
    setPreviewDocumentCategoryId(document.category_id ? Number(document.category_id) : null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocumentId(null);
  }, []);

  const handleDocumentNameClick = useCallback(
    (document: DocumentOrUpload) => {
      // Only allow interactions with real documents, not temporary uploads
      if ('isTemporary' in document) return;

      if (clickTimerRef.current === null) {
        // First click
        clickTimerRef.current = setTimeout(() => {
          // Single click - open preview
          handlePreviewDocument(document);
          clickTimerRef.current = null;
        }, 200); // 200ms threshold for double click
      } else {
        // Second click - double click
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
        handleStartEditing(document);
      }
    },
    [handlePreviewDocument, handleStartEditing]
  );

  const handleUploadClick = useCallback(() => {
    documentUploadButtonRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      // Ensure we have a selected property location before allowing upload
      if (!selectedPropertyLocation?.id) {
        enqueueSnackbar('Please select a property location before uploading documents', {
          variant: 'warning',
        });
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const defaultCategoryId = searchParams.get('defaultCategoryId');

      // Store category ID for later use after successful upload
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
        enqueueSnackbar('Document will be categorized as a lease agreement', { variant: 'info' });
      }

      // Create a temporary identifier for this upload
      const uploadId = `upload_${Date.now()}_${file.name}`;

      // Create temporary upload entry to show in the list immediately
      setTemporaryUploads(prev => ({
        ...prev,
        [uploadId]: {
          id: uploadId,
          original_name: file.name,
          created_at: new Date().toISOString(),
          category_id: null,
          tags: null,
          isTemporary: true,
          uploadProgress: {
            message: 'Preparing upload...',
            progress: 0,
            lastUpdate: Date.now(),
          },
        },
      }));

      uploadWithStreaming({
        file,
        location_ids: [selectedPropertyLocation.id],
        tags: {
          property_location_ids: [selectedPropertyLocation.id],
          building_ids: [],
          unit_ids: [],
          tenant_ids: [],
          property_manager_ids: [],
        },
      });
    },
    [uploadWithStreaming, selectedPropertyLocation, location.search]
  );

  // Check for openDocumentUpload query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openDocumentUpload') === 'true' && !dialogOpenedRef.current) {
      // Mark that we've opened the dialog for this session
      dialogOpenedRef.current = true;

      // Store defaultCategoryId if present
      const defaultCategoryId = searchParams.get('defaultCategoryId');
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
      }

      // Clear URL parameters immediately
      const newUrl = new URL(window.location.href);
      newUrl.search = '';
      history.replace(newUrl.pathname);

      // Open the upload dialog
      handleUploadClick();
    }
  }, [location.search, handleUploadClick, history]);

  // Refetch documents when selected property location changes
  useEffect(() => {
    if (selectedPropertyLocation?.id && session?.access_token) {
      refetch();
    }
  }, [selectedPropertyLocation?.id, session?.access_token, refetch]);

  // Check for stuck uploads every 30 seconds
  useEffect(() => {
    const checkStuckUploads = () => {
      const now = Date.now();
      const stuckThreshold = 60000; // 60 seconds without progress update

      // Check temporary uploads
      setTemporaryUploads(prev => {
        const updated = { ...prev };
        let hasStuckUploads = false;

        Object.keys(updated).forEach(key => {
          const upload = updated[key];
          const lastUpdate = upload.uploadProgress.lastUpdate || 0;

          if (now - lastUpdate > stuckThreshold && upload.uploadProgress.progress < 100) {
            hasStuckUploads = true;
            updated[key] = {
              ...upload,
              uploadProgress: {
                ...upload.uploadProgress,
                message: 'Upload may still be processing in background',
                lastUpdate: now,
              },
            };
          }
        });

        if (hasStuckUploads) {
          // Stuck uploads detected - they will show updated message to user
        }

        return updated;
      });

      // Check processing documents
      setUploadProgress(prev => {
        const updated = { ...prev };
        let hasStuckProcessing = false;

        Object.keys(updated).forEach(documentId => {
          const progress = updated[documentId];
          const lastUpdate = progress.lastUpdate || 0;

          if (now - lastUpdate > stuckThreshold && progress.progress < 100) {
            hasStuckProcessing = true;
            updated[documentId] = {
              ...progress,
              message: 'Processing appears stuck - document may still be processing in background',
              lastUpdate: now,
            };
          }
        });

        if (hasStuckProcessing) {
          // Stuck processing detected - they will show updated message to user
        }

        return updated;
      });
    };

    const interval = setInterval(checkStuckUploads, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Show message if no location is selected
  if (!selectedPropertyLocation?.id) {
    return (
      <MuiPageWrapper>
        <Box>
          <Stack direction="column" spacing={3}>
            <DashboardHeader hasInnerHtml title="Documents: <em>Active</em>" />
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Please select a property location to view documents.
            </Typography>
          </Stack>
        </Box>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Box>
        <Stack direction="column" spacing={3}>
          <Box
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              display: 'flex',
              justifyContent: { sm: 'space-between' },
              width: '100%',
            }}
          >
            <DashboardHeader hasInnerHtml title="Documents: <em>Active</em>" />
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={handleUploadClick}
              disabled={isUploading || isLoading || !selectedPropertyLocation?.id}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                alignSelf: isMobile ? 'stretch' : 'auto',
                py: isMobile ? 1 : 1.5,
                borderRadius: 1.5,
              }}
            >
              Upload Document
            </Button>
          </Box>

          <Typography variant="body1">
            View and manage documents for {selectedPropertyLocation.name}.
          </Typography>

          <DocumentUploadButton
            ref={documentUploadButtonRef}
            isFileUploading={isUploading}
            onFileSelect={handleFileSelect}
            sx={{ display: 'none' }}
          />

          <Card sx={{ bgcolor: 'common.white' }}>
            <DropZone onFileDrop={handleFileSelect} isUploading={isUploading}>
              <Toolbar
                sx={{
                  height: 64,
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: theme.spacing(2),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'common.white',
                }}
              >
                <OutlinedInput
                  fullWidth
                  value={filterName}
                  onChange={handleFilterName}
                  placeholder="Search documents..."
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchMdIcon sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  }
                  sx={{
                    maxWidth: 320,
                    bgcolor: 'common.white',
                  }}
                />

                <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Filter list">
                      <IconButton onClick={handleFilterClick}>
                        <FilterLinesIcon sx={{ fontSize: 'medium' }} />
                      </IconButton>
                    </Tooltip>
                    {(filters.category ||
                      filters.tags.length > 0 ||
                      filters.dateRange.start ||
                      filters.dateRange.end) && (
                      <Tooltip title="Reset filters">
                        <IconButton
                          size="medium"
                          onClick={handleFilterReset}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: theme.palette.error.light,
                              opacity: 0.9,
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.error.main,
                              },
                            },
                            '&:active': {
                              backgroundColor: theme.palette.error.light,
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          <RotateCcwIcon sx={{ fontSize: 'medium' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              </Toolbar>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map(headCell => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.id === '' ? 'right' : 'left'}
                          sortDirection={orderBy === headCell.id ? order : false}
                          sx={{
                            width: headCell.width,
                            borderRight: 'none',
                            '&:last-child': {
                              borderRight: 'none',
                            },
                            bgcolor: theme.palette.secondary[200],
                            textTransform: 'none',
                            color: 'text.primary',
                            '& .MuiTableSortLabel-root': {
                              textTransform: 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: { xs: '0.875rem', sm: 'inherit' },
                              fontWeight: 'bold',
                              color: 'text.primary',
                              '&:hover': {
                                color: 'text.primary',
                              },
                              '&.Mui-active': {
                                color: 'text.primary',
                                '&:hover': {
                                  color: 'text.primary',
                                },
                              },
                            },
                          }}
                        >
                          {headCell.sortable ? (
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={orderBy === headCell.id ? order : 'asc'}
                              onClick={() => handleRequestSort(headCell.id as keyof Document)}
                            >
                              {headCell.label}
                            </TableSortLabel>
                          ) : (
                            <Typography
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.875rem', sm: 'inherit' },
                                fontWeight: 'bold',
                                color: 'text.primary',
                                '&:hover': {
                                  color: 'text.primary',
                                },
                              }}
                            >
                              {headCell.label}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Loading documents...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading && displayedDocs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No documents found for this location.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading &&
                      displayedDocs.length > 0 &&
                      displayedDocs.map(row => {
                        const isTemporary = 'isTemporary' in row;
                        const isProcessing = !isTemporary && processingDocuments.has(row.id);

                        let progress: { message: string; progress: number } | undefined;
                        if (!isTemporary) {
                          progress = uploadProgress[row.id];
                        } else if (isTemporary) {
                          progress = row.uploadProgress;
                        }

                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              '&:hover': {
                                backgroundColor: `${theme.palette.secondary[200]} !important`,
                                cursor: isTemporary ? 'default' : 'pointer',
                                transition: 'background-color 0.2s ease',
                              },
                              '&:hover .delete-icon': {
                                opacity: !isTemporary && !isProcessing ? 1 : 0,
                              },
                              opacity: isTemporary ? 0.7 : 1,
                            }}
                          >
                            <TableCell sx={{ width: '30%' }}>
                              {editingDocumentId === row.id && !isTemporary ? (
                                <DocumentNameEditor
                                  initialValue={row.original_name}
                                  onSave={handleSaveDocumentName}
                                  onCancel={handleCancelEditing}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    cursor: isTemporary || isProcessing ? 'default' : 'pointer',
                                    userSelect: 'none',
                                  }}
                                  onClick={
                                    isTemporary || isProcessing
                                      ? undefined
                                      : () => handleDocumentNameClick(row)
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                      flex: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                          color: isTemporary ? 'inherit' : 'primary.main',
                                        },
                                      }}
                                      title={row.original_name}
                                    >
                                      {formatDocumentName(row.original_name)}
                                    </Typography>
                                    {progress && (
                                      <Box sx={{ width: '100%' }}>
                                        <Typography
                                          variant="caption"
                                          color="primary.main"
                                          sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                                        >
                                          🤖 AI Processing: {progress.message}
                                        </Typography>
                                        <LinearProgress
                                          variant={
                                            progress.progress > 0 ? 'determinate' : 'indeterminate'
                                          }
                                          value={progress.progress}
                                          sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: 'primary.lighter',
                                            '& .MuiLinearProgress-bar': {
                                              borderRadius: 3,
                                              backgroundColor: 'primary.main',
                                            },
                                          }}
                                        />
                                        {progress.progress > 0 && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                                          >
                                            {Math.round(progress.progress)}%
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                  {!isTemporary && !isProcessing && (
                                    <IconButton
                                      size="small"
                                      className="delete-icon"
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteClick(row.id);
                                      }}
                                      sx={{
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        p: 0.5,
                                        color: 'error.main',
                                        '&:hover': {
                                          backgroundColor: 'error.lighter',
                                        },
                                      }}
                                    >
                                      <Trash01Icon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '25%' }}>
                              {isTemporary ? (
                                <Typography variant="body2" color="text.secondary">
                                  Processing...
                                </Typography>
                              ) : (
                                <Select
                                  value={row.category_id?.toString() || ''}
                                  onChange={e => handleCategoryChange(row.id, e.target.value)}
                                  size="small"
                                  disabled={isProcessing}
                                  sx={{
                                    width: '100%',
                                    height: 32,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.secondary[400],
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.secondary[600],
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.secondary[600],
                                      borderWidth: '1px',
                                    },
                                    '& .MuiSelect-select': {
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      lineHeight: 1.2,
                                    },
                                  }}
                                >
                                  {categories.map(category => (
                                    <MenuItem key={category.id} value={category.id.toString()}>
                                      {category.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '30%' }}>
                              {isTemporary ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  gap={0.5}
                                  sx={{
                                    minHeight: 28,
                                    py: 0.5,
                                    '&:hover .edit-tags-icon': {
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  {row.tags?.map(tag => {
                                    let tagName = '';
                                    if (tag.property_location_id) {
                                      const propertyLocation = row.property_locations?.find(
                                        l => String(l?.id) === String(tag.property_location_id)
                                      );
                                      tagName = propertyLocation?.name || '';
                                    } else if (tag.building_id) {
                                      const building = row.buildings?.find(
                                        b => String(b?.id) === String(tag.building_id)
                                      );
                                      tagName = building?.name || '';
                                    } else if (tag.unit_id) {
                                      const unit = row.units?.find(
                                        u => String(u?.id) === String(tag.unit_id)
                                      );
                                      tagName = unit?.name || '';
                                    } else if (tag.tenant_id) {
                                      const tenant = row.tenants?.find(
                                        t => String(t?.id) === String(tag.tenant_id)
                                      );
                                      tagName = tenant?.full_name || '';
                                    } else if (tag.property_manager_id) {
                                      const manager = row.property_managers?.find(
                                        m => String(m?.id) === String(tag.property_manager_id)
                                      );
                                      tagName = manager?.limitedUser.full_name || '';
                                    }

                                    if (!tagName) {
                                      return null;
                                    }

                                    return (
                                      <Chip
                                        key={`${row.id}-${tag.id}`}
                                        label={tagName}
                                        size="small"
                                        sx={{
                                          height: 28,
                                          backgroundColor: 'transparent',
                                          '& .MuiChip-label': {
                                            px: 1.5,
                                            py: 0.5,
                                            fontSize: '0.75rem',
                                            lineHeight: 1.2,
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                  <IconButton
                                    size="small"
                                    className="edit-tags-icon"
                                    onClick={() => handleTagDocument(row)}
                                    disabled={isTemporary || isProcessing}
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      p: 0.5,
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                              {formatFullDate(new Date(row.created_at))}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                    sx={{ width: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {`${page * rowsPerPage + 1}-${Math.min(
                      (page + 1) * rowsPerPage,
                      filteredDocs.length
                    )} of ${filteredDocs.length}`}
                  </Typography>
                  <IconButton
                    onClick={() => handleChangePage(null, page - 1)}
                    disabled={page === 0}
                    size="small"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleChangePage(null, page + 1)}
                    disabled={page >= Math.ceil(filteredDocs.length / rowsPerPage) - 1}
                    size="small"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
              </Box>
            </DropZone>
          </Card>
        </Stack>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 500 },
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {isTagModalOpen && selectedDocument && (
        <VaultDocumentTagModal
          document={selectedDocument}
          onClose={handleTagModalClose}
          onSuccess={handleTagModalSuccess}
        />
      )}

      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 600 },
          },
        }}
      >
        <DialogTitle>Filter Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Category
              </Typography>
              <Select
                fullWidth
                value={filters.category}
                onChange={e => setFilters(prev => ({ ...prev, category: e.target.value }))}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[400],
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                    borderWidth: '1px',
                  },
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Date Added
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={filters.dateRange.start?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        start: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={filters.dateRange.end?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        end: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterReset}>Reset</Button>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button onClick={() => handleFilterApply(filters)} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {previewDocumentId && (
        <DocumentViewModal
          id={previewDocumentId}
          categoryId={previewDocumentCategoryId ?? undefined}
          onClose={handleClosePreview}
        />
      )}
    </MuiPageWrapper>
  );
};

export default VaultPage;
